import { ApplicationRef, createComponent, EnvironmentInjector, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { TutorialsStore, TutorialType } from '@store';
import { ViUIService } from '@vi-iot/ui';
import { ShepherdService } from 'angular-shepherd';
import { combineLatest } from 'rxjs';
import { filter, first } from 'rxjs/operators';
import Step from 'shepherd.js/src/types/step';
import { ShepherdCardComponent } from './components/shepherd-card/shepherd-card.component';
import { ShepherdStepCounterComponent } from './components/shepherd-step-counter/shepherd-step-counter.component';
import { SHEPHERD_CARD_TYPES } from './model';
import { InstallationsListShepherdScenario } from './scenarios/installations-list.scenario';
import { LandingPageShepherdScenario } from './scenarios/landing-page.scenario';
import { KanbanBoardScenario } from './scenarios/kanban-board.scenario';

@Injectable({
  providedIn: 'root',
})
export class ShepherdModalService {
  constructor(
    private injector: EnvironmentInjector,
    private appRef: ApplicationRef,
    private tutorialsStore: TutorialsStore,
    public shepherdService: ShepherdService,
    public viUIService: ViUIService,
    public translateService: TranslateService
  ) {
    this.shepherdService.defaultStepOptions = {
      classes: 'shepherd-modal',
      scrollTo: false,
      cancelIcon: {
        enabled: true,
      },
    };
  }

  public startTutorial(tutorialType: TutorialType): void {
    combineLatest([
      this.tutorialsStore.loaded$,
      this.tutorialsStore.hasTutorialBeenPresented(tutorialType),
    ])
      .pipe(
        filter(([loaded, hasBeenPresented]) => loaded && !hasBeenPresented),
        first()
      )
      .subscribe(() => {
        this.startShepherdScenario(tutorialType);
      });
  }

  private startShepherdScenario(tutorialType: TutorialType): void {
    let scenarioSteps: { (): Step.StepOptions[]; call?: any };

    switch (tutorialType) {
      case TutorialType.LandingPage:
        scenarioSteps = LandingPageShepherdScenario.getScenarioSteps;
        break;

      case TutorialType.KanbanInstallationList:
        scenarioSteps = InstallationsListShepherdScenario.getScenarioSteps;
        break;

      case TutorialType.KanbanBoard:
        scenarioSteps = KanbanBoardScenario.getScenarioSteps;
        break;
    }

    if (!this.shepherdService.tourObject?.isActive()) {
      this.shepherdService.modal = true;
      this.shepherdService.addSteps(scenarioSteps.call(this));
      this.shepherdService.start();
      this.viUIService.hideBodyScrollbar();

      this.shepherdService.tourObject.on('show', () => {
        const root = document.querySelector('body');
        root.classList.add('shepherd-tutorial-active');
      });

      this.shepherdService.tourObject.on('complete', () => {
        const root = document.querySelector('body');
        root.classList.remove('shepherd-tutorial-active');

        this.completeScenario(tutorialType);
      });

      this.shepherdService.tourObject.on('cancel', () => {
        const root = document.querySelector('body');
        root.classList.remove('shepherd-tutorial-active');

        this.completeScenario(tutorialType);
      });

      this.shepherdService.tourObject.on('hide', () => {
        const root = document.querySelector('body');
        root.classList.remove('shepherd-tutorial-active');
      });
    }
  }

  public attachShepherdCardElement(type: SHEPHERD_CARD_TYPES): void {
    const currentStepElement = this.shepherdService.tourObject?.getCurrentStep()?.getElement();
    const componentRef = createComponent(ShepherdCardComponent, {
      environmentInjector: this.injector,
    });

    componentRef.instance.type = type;

    currentStepElement?.insertBefore(
      componentRef.location.nativeElement,
      currentStepElement.querySelector('.shepherd-content')
    );

    this.appRef.attachView(componentRef.hostView);
    componentRef.changeDetectorRef.detectChanges();
  }

  public attachShepherdStepCounterElement(): void {
    const currentStep = this.shepherdService.tourObject?.getCurrentStep();
    const steps = this.shepherdService.tourObject?.steps;
    const currentStepElement = currentStep?.getElement();

    const componentRef = createComponent(ShepherdStepCounterComponent, {
      environmentInjector: this.injector,
    });

    componentRef.instance.stepNumber = steps.indexOf(currentStep) + 1;
    componentRef.instance.stepCount = steps.length;

    currentStepElement?.insertBefore(
      componentRef.location.nativeElement,
      currentStepElement.querySelector('.shepherd-content')
    );

    this.appRef.attachView(componentRef.hostView);
    componentRef.changeDetectorRef.detectChanges();
  }

  public scrollToElement(elementQuerySelector: string): void {
    const element = document.querySelector(elementQuerySelector);
    if (element) {
      const offset = 56;
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  }

  public scrollToTop(): void {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }

  public scrollToElementInContainer(
    containerQuery: string,
    targetQuery: string,
    offset = 300
  ): void {
    const container = document.querySelector(containerQuery);
    const target = document.querySelector(targetQuery);

    if (container && target) {
      const elementPosition = target.getBoundingClientRect().top;
      const offsetPosition = elementPosition - offset;

      container.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  }

  public scrollToRightEnd(element: Element): void {
    element.scrollLeft = element.scrollWidth;
  }

  public scrollToLeftEnd(element: Element): void {
    element.scrollLeft = 0;
  }

  private completeScenario(tutorialType: TutorialType): void {
    this.scrollToTop();
    this.viUIService.unhideBodyScrollbar();
    this.tutorialsStore.markTutorialAsPresented(tutorialType);
  }
}
